// /src/state — config repository. Reads the catalog + categories + current
// values; writes values and logs every write to config_events (§12.4).
//
// A "write" is read-modify-write so we can capture old→new for the event log
// and keep one live row per (param, scope, sub_key). Single-operator tool, so
// no concurrent-write contention to guard against this phase.
import { requireSupabase, describeSupabaseError } from './supabaseClient.js';

// Unwrap a supabase result, throwing a classified (specific) error on failure.
function ok({ data, error }) {
  if (error) throw new Error(describeSupabaseError(error));
  return data;
}

export async function fetchParameters() {
  return ok(await requireSupabase().from('parameters').select('*').order('sort_order'));
}

export async function fetchCategories() {
  return ok(await requireSupabase().from('categories').select('*').order('sort_order'));
}

export async function fetchConfigValues() {
  return ok(await requireSupabase().from('config_values').select('*'));
}

export async function fetchRecentEvents(limit = 100) {
  return ok(
    await requireSupabase()
      .from('config_events')
      .select('*')
      .order('changed_at', { ascending: false })
      .limit(limit),
  );
}

// Find the existing live-scope row (nulls matched explicitly for PostgREST).
async function findExisting(sb, { param_id, scope_tier, scope_key, sub_key }) {
  let q = sb.from('config_values').select('*').eq('param_id', param_id).eq('scope_tier', scope_tier);
  q = scope_key == null ? q.is('scope_key', null) : q.eq('scope_key', scope_key);
  q = sub_key == null ? q.is('sub_key', null) : q.eq('sub_key', sub_key);
  return ok(await q.maybeSingle());
}

async function logEvent(sb, ev) {
  ok(await sb.from('config_events').insert(ev));
}

// Set (insert or update) a config value and log the change.
// key = { param_id, scope_tier, scope_key, sub_key }
export async function setConfigValue(key, value, { note = null, expires_on = null, changedBy = null } = {}) {
  const sb = requireSupabase();
  const existing = await findExisting(sb, key);
  const now = new Date().toISOString();

  if (existing) {
    ok(
      await sb
        .from('config_values')
        .update({ value, note, expires_on, updated_at: now, updated_by: changedBy })
        .eq('id', existing.id),
    );
    await logEvent(sb, {
      ...key,
      action: 'update',
      old_value: existing.value,
      new_value: value,
      note,
      changed_by: changedBy,
    });
  } else {
    ok(await sb.from('config_values').insert({ ...key, value, note, expires_on, updated_by: changedBy }));
    await logEvent(sb, {
      ...key,
      action: 'set',
      old_value: null,
      new_value: value,
      note,
      changed_by: changedBy,
    });
  }
}

// Remove an override (revert to the next scope up / factory) and log it.
export async function clearConfigValue(key, { changedBy = null } = {}) {
  const sb = requireSupabase();
  const existing = await findExisting(sb, key);
  if (!existing) return;
  ok(await sb.from('config_values').delete().eq('id', existing.id));
  await logEvent(sb, {
    ...key,
    action: 'clear',
    old_value: existing.value,
    new_value: null,
    changed_by: changedBy,
  });
}
